import { useCallback, useContext } from "react";
import { datadogRum } from "@datadog/browser-rum";
import { UserInfoContext } from "../Context";

/**
 * Returns a `trackEvent(name, attributes?)` function that sends a custom event
 * to Datadog RUM. The standard attributes — `program` (global context) and the
 * user's `id`/`tags` (via datadogRum.setUser, both set in App.tsx) — ride along
 * on every RUM event automatically, so callers only pass event-specific data.
 *
 * The event is skipped until the user's identity has loaded, so an action can
 * never reach Datadog before datadogRum.setUser runs (e.g. a cold deep-link
 * straight to a page that tracks on mount, before /user/info resolves). The
 * returned function is memoized on uid, so a caller firing from a mount effect
 * with `[trackEvent]` in its deps will re-fire once the user becomes known.
 *
 * Safe to call in any environment — when RUM is not initialized (dev),
 * `addAction` is a no-op.
 */
export function useTrackEvent() {
  const { uid } = useContext(UserInfoContext);

  return useCallback(
    (name: string, attributes?: Record<string, unknown>) => {
      if (!uid) return;
      datadogRum.addAction(name, attributes);
    },
    [uid]
  );
}
